<template>
  <v-card>
    <v-card-title class="title">Authenticator App</v-card-title>
    <v-card-text>
      <!-- State 1: Not enrolled, toggle off -->
      <div v-if="!enrolled && !totpEnabled">
        Authenticator app sign-in is not enabled on this server.
      </div>

      <!-- State 2: Not enrolled, toggle on, not in setup flow -->
      <div v-else-if="!enrolled && !inSetup">
        <p>
          Use an authenticator app (Google Authenticator, Microsoft Authenticator,
          Okta Verify, Duo, etc.) for stronger sign-in than email codes.
        </p>
        <v-btn color="primary" @click="onStartSetup" :disabled="busy">Enable Authenticator App</v-btn>
      </div>

      <!-- State 3: Setup in progress (QR shown) -->
      <div v-else-if="inSetup">
        <p>Scan this QR code with your authenticator app:</p>
        <img v-if="qrDataUrl" :src="qrDataUrl" alt="QR code" style="display:block; margin:1em 0;" />
        <p>
          If you can't scan, enter this key manually:
          <code style="font-family:monospace; font-size:1.1em">{{ manualKey }}</code>
        </p>
        <p>Then enter the 6-digit code from your app:</p>
        <v-text-field v-model="confirmCode" label="6-digit code" maxlength="6" />
        <v-alert v-if="setupError" :value="true" color="error" outline icon="warning">{{ setupError }}</v-alert>
        <v-btn color="primary" @click="onConfirm" :disabled="busy || confirmCode.length !== 6">Confirm</v-btn>
        <v-btn flat @click="onCancelSetup" :disabled="busy">Cancel</v-btn>
      </div>

      <!-- State 4: Enrolled -->
      <div v-else>
        <p><v-icon color="success">check_circle</v-icon> Authenticator app is enabled.</p>
        <v-btn color="warning" @click="onStartSetup" :disabled="busy">Re-enroll on a new device</v-btn>
        <v-btn color="error" flat @click="onDisable" :disabled="busy">Disable</v-btn>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import QRCode from 'qrcode'
import { TotpEnrollmentService } from '@/services/totpEnrollment.service'

export default {
  name: 'authenticator-setup-card',
  props: {
    enrolled: { type: Boolean, default: false },
    totpEnabled: { type: Boolean, default: true }
  },
  data() {
    return {
      inSetup: false,
      manualKey: '',
      qrDataUrl: '',
      confirmCode: '',
      setupError: '',
      busy: false
    }
  },
  methods: {
    async onStartSetup() {
      this.busy = true
      this.setupError = ''
      try {
        const r = await TotpEnrollmentService.setup()
        this.manualKey = r.manualKey
        this.qrDataUrl = await QRCode.toDataURL(r.qrCodeUri)
        this.confirmCode = ''
        this.inSetup = true
      } catch (e) {
        this.setupError = (e && e.response && e.response.data && e.response.data.error) || 'Could not start setup.'
      } finally {
        this.busy = false
      }
    },
    async onConfirm() {
      this.busy = true
      this.setupError = ''
      try {
        await TotpEnrollmentService.confirm(this.manualKey, this.confirmCode.trim())
        this.inSetup = false
        this.manualKey = ''
        this.qrDataUrl = ''
        this.confirmCode = ''
        this.$emit('enrolled')
      } catch (e) {
        this.setupError = (e && e.response && e.response.data && e.response.data.error) || 'Could not confirm.'
      } finally {
        this.busy = false
      }
    },
    onCancelSetup() {
      this.inSetup = false
      this.manualKey = ''
      this.qrDataUrl = ''
      this.confirmCode = ''
      this.setupError = ''
    },
    async onDisable() {
      this.busy = true
      try {
        await TotpEnrollmentService.disable()
        this.$emit('disabled')
      } catch (e) {
        this.setupError = (e && e.response && e.response.data && e.response.data.error) || 'Could not disable.'
      } finally {
        this.busy = false
      }
    }
  }
}
</script>
